import { useState, useCallback, useEffect } from 'react';
import { useNotification } from '@/stores/notificationStore';
import { HttpAdminAdapter } from '@/services/admin/HttpAdminAdapter';
import type {
  AgentPrompt,
  UpdateAgentPromptRequest,
  AddPromptVersionRequest,
  SyncFromCodeResponse,
} from '@/services/admin/AdminService';

/**
 * Hook for managing agent prompts with version control
 */
export const useAgentPrompts = () => {
  const [adminService] = useState(() => new HttpAdminAdapter());
  const [prompts, setPrompts] = useState<AgentPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { showNotification } = useNotification();

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.listAgentPrompts();
      setPrompts(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch agent prompts';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminService]);

  const getPrompt = useCallback(
    async (agentName: string): Promise<AgentPrompt | null> => {
      try {
        return await adminService.getAgentPrompt(agentName);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch agent prompt';
        showNotification(errorMessage, 'error');
        return null;
      }
    },
    [adminService, showNotification]
  );

  const updatePrompt = useCallback(
    async (
      agentName: string,
      data: UpdateAgentPromptRequest
    ): Promise<AgentPrompt | null> => {
      setActionLoading(true);
      try {
        const updated = await adminService.updateAgentPrompt(agentName, data);
        setPrompts((prev) =>
          prev.map((p) => (p.agent_name === agentName ? updated : p))
        );
        showNotification('Agent prompt updated successfully', 'success');
        return updated;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to update agent prompt';
        showNotification(errorMessage, 'error');
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [adminService, showNotification]
  );

  const addVersion = useCallback(
    async (
      agentName: string,
      data: AddPromptVersionRequest
    ): Promise<AgentPrompt | null> => {
      setActionLoading(true);
      try {
        const updated = await adminService.addAgentPromptVersion(
          agentName,
          data
        );
        setPrompts((prev) =>
          prev.map((p) => (p.agent_name === agentName ? updated : p))
        );
        showNotification(
          `New version ${updated.current_version} created`,
          'success'
        );
        return updated;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to add version';
        showNotification(errorMessage, 'error');
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [adminService, showNotification]
  );

  const rollbackVersion = useCallback(
    async (agentName: string, version: string): Promise<AgentPrompt | null> => {
      setActionLoading(true);
      try {
        const updated = await adminService.rollbackAgentPromptVersion(
          agentName,
          version
        );
        setPrompts((prev) =>
          prev.map((p) => (p.agent_name === agentName ? updated : p))
        );
        showNotification(`Rolled back to version ${version}`, 'success');
        return updated;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to rollback version';
        showNotification(errorMessage, 'error');
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [adminService, showNotification]
  );

  const syncFromCode = useCallback(
    async (preview: boolean = false): Promise<SyncFromCodeResponse | null> => {
      setActionLoading(true);
      try {
        const response = await adminService.syncAgentPromptsFromCode(preview);
        if (!preview && response.agents_synced > 0) {
          // Refresh prompts after sync
          await fetchPrompts();
        }
        const action = preview ? 'Preview' : 'Sync';
        const count = preview
          ? response.agents_with_changes
          : response.agents_synced;
        showNotification(
          `${action}: ${count} agent(s) ${preview ? 'have changes' : 'synced'}`,
          count > 0 ? 'success' : 'info'
        );
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to sync from code';
        showNotification(errorMessage, 'error');
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [adminService, showNotification, fetchPrompts]
  );

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  return {
    prompts,
    loading,
    error,
    actionLoading,
    fetchPrompts,
    getPrompt,
    updatePrompt,
    addVersion,
    rollbackVersion,
    syncFromCode,
  };
};

export default useAgentPrompts;
